/**
 * What each organisation-wide affordance requires: questions with no record to ask about, named once so
 * every surface asking the same question shares one rule.
 */
import { all, ldFlag, orgFlag } from './availability'
import { isSystemAdmin, managesConfigArea, managesInsights, promotesToSuperAdmin, type FgaAuthorizationRule } from './fgaRules'
import { adminsOnly, nonViewerAdmins, superAdmin, superAdminOrP2pAdmin } from './roleAuthorization'
import type { NonRecordAccessRule } from './accessRules'

/**
 * An action the endpoints reserve for a system admin, such as a bulk import, which writes many records
 * without the per-record checks an area grant would otherwise stand in for.
 */
export const SYSTEM_ADMIN = {
  authorization: { kind: 'fgaOrRole', fga: isSystemAdmin, role: superAdmin },
} as const satisfies NonRecordAccessRule

/**
 * Offering the Super Admin role when editing or adding a user; the endpoints refuse a promotion the caller
 * cannot make. Before the service decides, the product's own rule applies: only a super admin makes super admins.
 */
export const PROMOTE_TO_SUPER_ADMIN = {
  authorization: { kind: 'fgaOrRole', fga: promotesToSuperAdmin, role: superAdmin },
} as const satisfies NonRecordAccessRule

/**
 * A write in the insights section — creating or editing a dashboard, saving a widget into one, managing who
 * a dashboard is shared with. The endpoints check `can_manage_insights`, and before FGA they excluded
 * view-only admins, which the role arm keeps.
 */
export const MANAGE_INSIGHTS = {
  authorization: { kind: 'fgaOrRole', fga: managesInsights, role: nonViewerAdmins },
} as const satisfies NonRecordAccessRule

/**
 * Mirrors `generateQuestionInsights` and `dismissQuestionInsight`, which admit only superAdmin and admin.
 * Unrelated to `MANAGE_INSIGHTS`, which is the reporting-dashboards surface.
 */
export const MANAGE_QUESTION_INSIGHTS = {
  authorization: { kind: 'role', role: nonViewerAdmins },
} as const satisfies NonRecordAccessRule

/**
 * Mirrors `listGlobalAgentTemplates`. The RFx questionnaire builder opens the library to every RFx owner,
 * a standard user among them, whom the endpoint refuses.
 */
const managesAgentTemplateConsumers: FgaAuthorizationRule = (sources) =>
  managesConfigArea('organisation_settings')(sources) ||
  managesConfigArea('workflows_and_forms')(sources) ||
  managesConfigArea('document_review')(sources)

export const READ_AGENT_TEMPLATES = {
  authorization: { kind: 'fgaOrRole', fga: managesAgentTemplateConsumers, role: adminsOnly },
} as const satisfies NonRecordAccessRule

/**
 * Mirrors `getBudgetApprovalsGroups`. The group names serve the Dynamic Assignees settings and the workflow and
 * form editors, so a manager of either area reads them; the other surfaces that mount the shared hooks drop the list.
 */
const managesBudgetApprovalGroupConsumers: FgaAuthorizationRule = (sources) =>
  managesConfigArea('dynamic_assignees')(sources) || managesConfigArea('workflows_and_forms')(sources)

export const READ_BUDGET_APPROVAL_GROUPS = {
  authorization: { kind: 'fgaOrRole', fga: managesBudgetApprovalGroupConsumers, role: superAdminOrP2pAdmin },
} as const satisfies NonRecordAccessRule

/** The list endpoint admits admins and super admins only, so a view-only admin would get a 403. */
export const VIEW_PROACTIVE_SUGGESTIONS = {
  availability: all([orgFlag('RFX'), ldFlag('RELEASE_PROACTIVE_SOURCING')]),
  authorization: { kind: 'role', role: nonViewerAdmins },
} as const satisfies NonRecordAccessRule
